import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import CustomButton from '../../buttons/CustomButton';
import { ButtonTypes, PaymentResult } from '../../../services/exports/Interfaces';
import useSpreedlyQrPolling, { msUntilQrExpiry } from '../../../hooks/payment/useSpreedlyQrPolling';
import useDialogFocus from '../../../hooks/ui/useDialogFocus';
import type { SpreedlyQrSheetState } from '../../../hooks/payment/useSpreedlyOffsitePayment';

interface Props {
  sheet: SpreedlyQrSheetState;
  onSettled: (result: PaymentResult) => void;
  onCancel: () => void;
}

/**
 * The Pix code for a pending purchase. On a phone the customer copies the "copy and paste" code into
 * their bank app; on a computer they scan it. The sheet waits for the acquirer to report the payment
 * and closes itself; an expired code cannot be paid, so the customer starts again. Closing the
 * expired sheet is their decision, not a failure to report.
 *
 * The code is also printed as selectable text: where the clipboard is refused (an in-app browser,
 * a denied permission), copying falls back to selecting it for the customer.
 */
export default function SpreedlyQrSheet(props: Props) {
  const { sheet, onSettled, onCancel } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Components:Payment:Spreedly:SpreedlyQrSheet' });
  const { expired, checking, closeAfterCheck } = useSpreedlyQrPolling(sheet, onSettled);
  const [copied, setCopied] = useState(false);
  const payloadRef = useRef<HTMLParagraphElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const remaining = useRemainingSeconds(sheet.expiresAt);

  useDialogFocus(panelRef);

  // Closing asks once more first: a code paid in its last seconds is a paid order.
  const close = () => closeAfterCheck(onCancel);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  });

  const copy = async () => {
    if (!sheet.qrPayload) {
      return;
    }

    try {
      await navigator.clipboard.writeText(sheet.qrPayload);
      setCopied(true);
    } catch {
      setCopied(false);
      selectPayload();
    }
  };

  const selectPayload = () => {
    const selection = window.getSelection();

    if (!payloadRef.current || !selection) {
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(payloadRef.current);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  return (
    <div className={overlay} data-test="spreedly-qr-sheet">
      <div ref={panelRef} tabIndex={-1} className={panel} role="dialog" aria-modal="true" aria-label={t('title')}>
        <p className={title}>{t('title')}</p>
        {expired ? (
          <p className={errorText} role="alert" data-test="spreedly-qr-expired">
            {t('expired')}
          </p>
        ) : (
          <>
            <p className={hint}>{t('hint')}</p>
            <div className="flex justify-center py-sm bg-white rounded-md" data-test="spreedly-qr-code">
              {sheet.qrPayload ? (
                <QRCode value={sheet.qrPayload} size={184} />
              ) : (
                <img src={sheet.qrImageUrl} alt={t('qr_alt')} className="h-[184px] w-[184px]" />
              )}
            </div>
            {sheet.qrPayload && (
              <p ref={payloadRef} className={payloadText} data-test="spreedly-qr-payload">
                {sheet.qrPayload}
              </p>
            )}
            {sheet.qrPayload && (
              <CustomButton
                buttonType={ButtonTypes.primary}
                title={copied ? t('copied') : t('copy')}
                onClick={copy}
                className="mt-md w-full"
                testId="spreedly-qr-copy"
              />
            )}
            {remaining !== null && (
              <p className={hint} role="timer" data-test="spreedly-qr-countdown">
                {t('expires_in', { time: `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}` })}
              </p>
            )}
            <p className={hint} role="status">
              {t('waiting')}
            </p>
            {sheet.checkoutUrl && (
              <a href={sheet.checkoutUrl} target="_blank" rel="noreferrer" className={link}>
                {t('open_page')}
              </a>
            )}
          </>
        )}
        {checking && (
          <p className={hint} role="status" data-test="spreedly-qr-checking">
            {t('checking')}
          </p>
        )}
        <button
          type="button"
          className={cancelButton}
          onClick={close}
          disabled={checking}
          data-test="spreedly-qr-cancel"
        >
          {expired ? t('close') : t('cancel')}
        </button>
      </div>
    </div>
  );
}

function useRemainingSeconds(expiresAt: string | null): number | null {
  const compute = () => {
    const ms = msUntilQrExpiry(expiresAt);

    return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 1000)) : null;
  };

  const [remaining, setRemaining] = useState<number | null>(compute);

  useEffect(() => {
    if (compute() === null) {
      return;
    }

    const timer = setInterval(() => setRemaining(compute()), 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  return remaining;
}

const overlay = 'fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-md';
const panel = 'bg-brand-background-primary rounded-lg p-md w-full max-w-[420px]';
const title = 'text-md font-semibold mb-xs text-brand-text-primary dark:text-white';
const hint = 'text-sm mt-sm text-brand-text-primary opacity-80 dark:text-white';
const errorText = 'mt-sm text-sm text-danger';
const payloadText =
  'mt-sm select-all break-all rounded-md bg-brand-background-secondary p-xs font-mono text-xs text-brand-text-primary dark:text-white';
const link = 'mt-sm block text-center text-sm underline underline-offset-2 text-brand-text-primary dark:text-white';
const cancelButton =
  'mt-sm block w-full text-center text-sm font-medium underline underline-offset-2 text-brand-text-primary hover:opacity-80 disabled:opacity-50 dark:text-white';
